import React, {useState, useEffect} from 'react';
import { useTranslation } from 'react-i18next';
import iconRight from "url:../../assets/icons/chevronRight.svg"
import iconLeft from "url:../../assets/icons/chevronLeft.svg"
import iconBadge from "url:../../assets/icons/badge-check.svg"
import iconCalendar from "url:../../assets/icons/calendar.svg"
import {formatYYYYDDMM} from "../../helpers/dateFunctions";
import CustomButton from "../CustomButton"
import DatePicker from "../DatePicker"
import Toaster from "../Toaster"
import iconClose from "url:../../assets/icons/close.svg";

interface ApiCallResponse {
  response: Response,
  json?: any,
  isExpectedError: boolean,
  errorName?: string,
  errorMessage?: string,
}

interface ApiInterface {
  get: (url: string) => Promise<ApiCallResponse>;
  post: (url: string, data: any) => Promise<ApiCallResponse>;
}

interface WebSocketClientInterface {
  send: (message: any) => void;
  webSocket?: WebSocket
}

interface Props {
  who: string
  api: ApiInterface
  meetingId: string
  close: Function
  lang: string
  partnerId: string
  webSocketClient: WebSocketClientInterface
}

interface FocusAreaInterface {
  id: string;
  name: string;
}

const imageNrs = Array.from(new Array(58),(val,index)=> "" + (index + 1));

const CoachingTools: React.FC<Props> = ({ who, api, meetingId, close, lang, webSocketClient, partnerId }) => {
  // const api = Api.getInstance();
  const translation = useTranslation();
  const { t } = translation;
  const [page, setPage] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [activity, setActivity] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [images, setImages] = useState<Array<string>>([])
  const [selectImages, setSelectImages] = useState<Array<string>>([])
  const [focusArea, setFocusArea] = useState<string>('')
  const [deadline, setDeadline] = useState<Date|null>(null)
  const [selectDeadline, setSelectDeadline] = useState<boolean>(false)
  const [imagesChanged, setImagesChanged] = useState<boolean>(false)
  const [showToaster, setShowToaster] = useState<boolean>(false)
  const [focusAreas, setFocusAreas] = useState<Array<FocusAreaInterface>>([])

  const url = who + '/coaching-tools/' + meetingId

  useEffect(() => {
    getData()
    webSocketClient.webSocket?.addEventListener("message", messageListener)
    return () => {
      webSocketClient.webSocket?.removeEventListener("message", messageListener)
    }
  }, [])

  const getData = () => {
    api.get(url).then(response => {
      if (response.json.error) {
        alert(response.json.error)
      } else {
        setFocusAreas(response.json.focusAreas)
        if (response.json.meetingNotes) {
          setNotes(response.json.meetingNotes.notes)
          if (response.json.meetingNotes.images) {
            setImages(response.json.meetingNotes.images)
            setSelectImages(response.json.meetingNotes.images)
          }
        }
      }
    })
  }

  const messageListener = (event: any) =>  {
    const mess = JSON.parse(event.data)
    if (!mess) return
    if (mess.command === 'updatedCoachingTools') {
      getData()
    }
    // console.log(mess)
  }

  const updateDeadline = (d: Date) => {
    setDeadline(d)
    setSelectDeadline(false)
  }

  const notifyPeer = () => {
    const mess = {
      type: 'to_user_id',
      user_id: partnerId,
      message: {
        type: 'from_user_id',
        command: 'updatedCoachingTools',
      }
    }
    webSocketClient.send(mess)
  }

  const saveActivity = () => {
    const data = {
      what: 'createActivity',
      focusAreaId: focusArea,
      title: title,
      activity: activity,
      deadline: deadline,
    }

    api.post(url, data).then(response => {
      if (response.json.error) {
        alert(response.json.error)
      } else {
        setShowToaster(true)
        setDeadline(null)
        setActivity('')
        setTitle('')
        setFocusArea('')
        notifyPeer()
      }
    })
  }

  const saveNotes = () => {
    const data = {
      what: 'notes',
      notes: notes
    }

    api.post(url, data).then(response => {
      if (response.json.error) {
        alert(response.json.error)
      } else {
        console.log('Saved notes')
        notifyPeer()
      }
    })
  }

  const selectImage = (nr: string) => {
    let found = false
    for (let _nr of selectImages) {
      if (_nr === nr) {
        found = true
      }
    }
    if (!found) {
      // Add image
      setSelectImages([...selectImages, nr])
    } else {
      let _images: Array<string> = []
      for (let _nr of selectImages) {
        if (_nr !== nr) {
          _images.push(_nr)
        }
      }
      setSelectImages(_images)
    }
    setImagesChanged(true)
  }

  const saveImages = (images: Array<string>) => {
    const data = {
      what: 'images',
      images: images
    }

    api.post(url, data).then(response => {
      if (response.json.error) {
        alert(response.json.error)
      } else {
        setImages(images)
        setPage('reflect')
        setImagesChanged(false)
        notifyPeer()
      }
    })
  }

  const removeImage = (imgId: string) => {
    const _images: Array<string> = []
    for (const img of images) {
      if (img !== imgId) {
        _images.push(img)
      }
    }
    setSelectImages(_images)
    saveImages(_images)
  }

  if (page === 'activity') {
    return (
      <>
        <div className="flex justify-between items-center">
          <img src={iconLeft} className="cursor-pointer h-6 w-6" alt="left"
               onClick={() => setPage('')}/>
          <div className="text-lg font-medium">
            {lang === 'sv' ? 'Skapa aktivitet' : 'Create action'}
          </div>
          <img src={iconClose} className="cursor-pointer h-6 w-6" alt="close"
               onClick={() => close()}/>
        </div>
        <hr className="mt-4 border-neutral-700"/>

        <h2 className="text-2xl font-medium mt-6">
          {lang === 'sv' ? 'Formulera aktivitet' : 'Formulate action'}
        </h2>

        <p className="mt-2 text-neutral-400">
          {
            lang === 'sv' ?
              'Tips! Formulera aktiviteten i Jag-form och skriv den konkret så det blir tydligt vad som ska göras och när det ska vara klart.'
              :
              'Recommendation! Write the Action in first person (I-form) and make it specific so it\'s clear what needs to be done and when it should be completed.'
          }
        </p>
        <p className="mt-6 font-medium">
          {lang === 'sv' ? 'Titel' : 'Title'}
        </p>
        <input type="text" className="mt-2 z-input" value={title}
               placeholder={lang === 'sv' ? 'Skriv här...' : 'Write here...'}
               onChange={(e) => setTitle(e.target.value)}/>

        <p className="mt-4 font-medium">
          {lang === 'sv' ? 'Formulera aktivitet' : 'Formulate activity'}
        </p>
        <textarea rows={8} className="mt-2 z-input" value={activity}
                  placeholder={lang === 'sv' ? 'Skriv här...' : 'Write here...'}
                  onChange={(e) => setActivity(e.target.value)}/>

        {/*<p className="mt-6 font-medium">*/}
        {/*  {lang === 'sv' ? 'Fokusområde' : 'Focus Area'}*/}
        {/*</p>*/}
        {/*<select className="mt-2 z-input" value={focusArea}*/}
        {/*        onChange={(e) => setFocusArea(e.target.value)}>*/}
        {/*  <option value=''>{lang === 'sv' ? 'Välj' : 'Select'}</option>*/}
        {/*  {*/}
        {/*    focusAreas.map(focusArea => (*/}
        {/*      <option key={"fa_" + focusArea.id} value={focusArea.id}>{focusArea.name}</option>*/}
        {/*    ))*/}
        {/*  }*/}
        {/*</select>*/}

        <p className="mt-6 font-medium">
          {
            lang === 'sv' ? 'Måldatum' : 'Target date'
          }
        </p>
        {
          selectDeadline &&
            <DatePicker dates={[]} inactiveDatesRange={[new Date()]} clickDate={updateDeadline} preSelectedDate={deadline}
                        skipClickMonthChange={true}/>
        }
        <div className="mt-2 z-input flex justify-between items-center" onClick={() => setSelectDeadline(true)}>
          <p className="text-neutral-400">
            {deadline ? formatYYYYDDMM(deadline) : lang === 'sv' ? 'Välj datum' : 'Select date'}
          </p>
          <img src={iconCalendar} alt="calendar" className="h-4 w-4"/>
        </div>

        <CustomButton buttonType="blueBase" onClick={saveActivity} className="mt-6">
          {lang === 'sv' ? 'Skapa aktivitet' : 'Create action'}
        </CustomButton>

        {
          showToaster &&
            <Toaster message={lang === 'sv' ? 'Aktivitet skapad' : 'Action created'} isOpen={showToaster}
                     image={iconBadge} onClose={() => setShowToaster(false)}/>
        }
      </>
    )
  } else if (page === 'reflect') {
    return (
      <>
        <div className="flex justify-between items-center">
          <img src={iconLeft} className="cursor-pointer h-6 w-6" alt="left"
               onClick={() => setPage('')}/>
          <div className="text-lg font-medium">
            {lang === 'sv' ? 'Reflektionsyta' : 'Reflection space'}
          </div>
          <img src={iconClose} className="cursor-pointer h-6 w-6" alt="close"
               onClick={() => close()}/>
        </div>
        <hr className="mt-4 border-neutral-700"/>

        <h2 className="mt-6 text-2xl font-medium">
          {lang === 'sv' ? 'Anteckningar' : 'Notes'}
        </h2>

        <textarea rows={8} className="mt-2 z-input" value={notes} onBlur={() => saveNotes()}
                  placeholder={lang === 'sv' ? 'Skriv dina tankar eller insikter här...' : 'Write your thoughts or insights here...'}
                  onChange={(e) => setNotes(e.target.value)}/>

        <p className="mt-2 text-neutral-400 text-sm">
          {
            lang === 'sv' ? 'Dina anteckningar sparas löpande' : 'Your notes are saved continuously'
          }
        </p>


        <div className="mt-6 text-xl">
          {lang === 'sv' ? 'Bilder' : 'Images'}
        </div>

        {
          images.map(nr => {
            return (
              <div className="mt-2 w-full relative"  key={"img_" + nr}>
                <div className="absolute right-0 top-0">
                  <div className="m-2 p-1 rounded-full bg-black cursor-pointer"
                       onClick={() => removeImage(nr)}>
                    <img src={iconClose} alt="remove" />
                  </div>
                </div>
                <img src={"https://public.zebrain.se/visionboard/pictures/" + nr + ".jpeg"}
                     className="w-full rounded-[6px] " alt={"image " + nr}/>
              </div>

            )
          })
        }

        <CustomButton buttonType="outlineWhiteBase" onClick={() => setPage('images')} className="mt-2">
          {lang === 'sv' ? 'Lägg till bilder' : 'Add images'}
        </CustomButton>

      </>
    )
  } else if (page === 'images') {
    return (
      <>
        <div className="flex justify-between items-center">
          <img src={iconLeft} className="cursor-pointer h-6 w-6" alt="left"
               onClick={() => setPage('reflect')}/>
          <div className="text-lg font-medium">
            {lang === 'sv' ? 'Välj bilder' : 'Select images'}
          </div>
          <img src={iconClose} className="cursor-pointer h-6 w-6" alt="close"
               onClick={() => close()}/>
        </div>
        <hr className="mt-4 border-neutral-700"/>

        <div className="flex gap-4 flex-wrap">
          {
            imageNrs.map(nr => {
              const bColor = selectImages.indexOf(nr) > -1 ?
                "border-primary-500" : "border-neutral-900"
              return (
                <img src={"https://public.zebrain.se/visionboard/pictures/thumbs/" + nr + ".jpeg"} key={"img_" + nr}
                     className={"border-[2px] rounded-[6px] cursor-pointer hover:border-primary-500 " + bColor} onClick={() => selectImage(nr)}
                     style={{width: "calc(50% - 8px)"}} alt={"image " + nr}/>
              )
            })
          }
          <div className="mt-8">&nbsp;</div>
        </div>


        <div className="video_extra_window_bottom">
          <div className="my-2 bg-neutral-900 pt-2">
            <CustomButton buttonType="blueBase" disabled={!imagesChanged}
                          onClick={() => saveImages(selectImages)}>
              {lang === 'sv' ? 'Spara' : 'Save'}
            </CustomButton>
          </div>

        </div>
      </>
    )
  }

  return (
    <>
      <div className="flex justify-between items-center">
        <div>&nbsp;</div>
        <div className="text-lg font-medium">
          {lang === 'sv' ? 'Coachingverktyg' : 'Coaching tools'}
        </div>
        <img src={iconClose} className="cursor-pointer h-6 w-6" alt="close"
             onClick={() => close()}/>
      </div>
      <hr className="mt-4 border-neutral-700"/>
      <h2 className="mt-6 text-2xl font-medium">
        {lang === 'sv' ? 'Välj verktyg' : 'Choose tool'}
      </h2>

      <div className="flex mt-6 justify-between items-center cursor-pointer" onClick={() => setPage('activity')}>
        <p>
          {lang === 'sv' ? 'Skapa aktivitet' : 'Create action'}
        </p>
        <img src={iconRight} alt="right" className="h-6 w-6"/>
      </div>
      <hr className="mt-4 border-neutral-700"/>

      <div className="flex mt-6 justify-between items-center cursor-pointer" onClick={() => setPage('reflect')}>
        <p>
          {lang === 'sv' ? 'Reflektionsyta' : 'Reflection space'}
        </p>
        <img src={iconRight} alt="right" className="h-6 w-6"/>
      </div>
      <hr className="mt-4 border-neutral-700"/>
    </>
  );
};

export default CoachingTools;
